/*
 * lab-land-map.js — <lab-land-map>, the map of the land the olives come from
 * (docs/superpowers/specs/2026-09-23-lab-land-map-design.md §3; markup in snippets/lab-land-map.liquid).
 *
 * The element holds a poster (a pre-rendered image of the ground, tools/lab/map/build.mjs), the
 * map's marks as HTML over it (snippets/lab-land-map-marks.liquid: context names, the four towns,
 * the mill, placed fields), © OpenStreetMap and a JSON data island. The marks are decluttered from
 * the start (on every resize, and when the fonts are in). Nothing heavy loads until the element
 * comes within 300 px of the viewport; then the vendored libraries (MapLibre GL + pmtiles,
 * assets/lab-maplibre-gl.js, lab-pmtiles.js) load once per page, the prebuilt style
 * (assets/lab-land-map-style.json) gets the tiles URL, the overlays (assets/lab-land-map-overlays.json)
 * are drawn in the label's grammar and the map mounts under the poster. On the map's first `idle`
 * (never on a timer), in one task, the same mark nodes become MapLibre markers, the attribution
 * becomes a map control and the poster goes: only the ground's pixels change.
 *
 *  - Fields (from the lot register, placed by Liquid) become real <button>s: click or Enter opens a
 *    label-style card with the lots; Escape or the card's × closes it and returns focus to the field.
 *  - Marks are HTML (the style has no symbol layers, so no glyphs or sprite are hosted). Declutter:
 *    a town name that would run under the mill goes to the other side of its square, one that would
 *    leave the frame wraps; context names come after towns, the mill and fields, villages before
 *    hamlets, and one that would touch a name already shown, or leave the frame, is hidden.
 *  - The camera matches the poster: the posters are drawn at 600 × 450 (4:3) and 400 × 500 (4:5)
 *    CSS px at the overlays' view zoom, centred on view.center, and scaled to the stage, so the
 *    live zoom is the view zoom shifted by log2(stage width / poster width).
 *  - Loading: from 1024 px the map mounts when the element comes within 300 px of the viewport (as
 *    before). Below 1024 the poster is the map until the reader presses its "Mappa interattiva"
 *    button: a phone never downloads MapLibre and the tiles for a map it only glances at. The map
 *    block (blocks/_lab-map.liquid) can ask for the button at every width (data-interaction="tap")
 *    or for no live map at all ("poster"); its other switches arrive as the data island's `layers`
 *    (municipal ground and borders, the field lines) and as lmap--no-* classes on the figure.
 *  - A found lot (assets/lab-lot.js, `lab:lot-field` with the field's name, the lot's code and
 *    whether the reader just asked) selects its field: the mark is marked current on the poster at
 *    once, and on the live map the camera frames that field together with the mill, the field's
 *    straight connecting line is drawn full and the others fade. A miss (name null) clears it and
 *    restores the default view. The line is a straight line between two stored points, never a road.
 *  - Reduced motion: MapLibre already jumps instead of easing for non-essential camera moves
 *    (zoom buttons, inertia) under prefers-reduced-motion. The only camera move this file starts
 *    is the framing of a lot the reader has just looked up on this page; it eases, except under
 *    reduced motion or on arrival (a ?lot= in the URL), where it jumps.
 *  - Failure (a library, the style, the overlays, the tiles): the poster stays and the element
 *    logs one console.warn. After the map is up, later errors (a tile) are warned once, too.
 *
 * Listeners on the document/element go with an AbortController; the map is removed on disconnect.
 */
(() => {
  if (customElements.get('lab-land-map')) return;

  // ---- vendored libraries, loaded once per page -------------------------------------------------------
  const scripts = new Map();
  const loadScript = (src) => {
    if (!scripts.has(src)) {
      scripts.set(src, new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener('error', () => reject(new Error(`could not load ${src}`)), { once: true });
        document.head.append(script);
      }));
    }
    return scripts.get(src);
  };

  // MapLibre's stylesheet is unlayered; imported as is it would beat every override in
  // @layer sections (assets/lab-section-map.css). It is fetched and put in @layer compat, which
  // lab-tokens.css orders before components and sections.
  let cssOnce = null;
  const loadCss = (href) => {
    cssOnce ??= fetch(href).then((response) => {
      if (!response.ok) throw new Error(`could not load ${href} (${response.status})`);
      return response.text();
    }).then((css) => {
      const style = document.createElement('style');
      style.dataset.labMaplibre = '';
      style.textContent = `@layer compat {\n${css}\n}`;
      document.head.append(style);
    });
    return cssOnce;
  };

  let libsOnce = null;
  const loadLibs = (el) => {
    libsOnce ??= Promise.all([
      loadScript(el.dataset.maplibreJs),
      loadScript(el.dataset.pmtilesJs),
      loadCss(el.dataset.maplibreCss),
    ]).then(() => {
      const { maplibregl, pmtiles } = window;
      if (!maplibregl || !pmtiles) throw new Error('map libraries did not define their globals');
      maplibregl.addProtocol('pmtiles', new pmtiles.Protocol().tile);
      return maplibregl;
    });
    return libsOnce;
  };

  const fetchJson = async (url, signal) => {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`${url}: ${response.status}`);
    if (!/json/.test(response.headers.get('content-type') || '')) throw new Error(`${url}: not JSON`);
    return response.json();
  };
  const fetchText = async (url, signal) => {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`${url}: ${response.status}`);
    return response.text();
  };

  // Brand tokens may be oklch(), which MapLibre's colour parser does not read: a 1 px canvas
  // resolves any CSS colour the browser understands into rgb().
  const toRgb = (() => {
    let ctx = null;
    return (value, fallback) => {
      ctx ??= Object.assign(document.createElement('canvas'), { width: 1, height: 1 }).getContext('2d', { willReadFrequently: true });
      if (!ctx || !value) return fallback;
      ctx.clearRect(0, 0, 1, 1);
      ctx.fillStyle = fallback;
      ctx.fillStyle = value.trim();
      ctx.fillRect(0, 0, 1, 1);
      const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
      return `rgb(${r}, ${g}, ${b})`;
    };
  })();

  const isView = (v) => v && Array.isArray(v.center) && Number.isFinite(v.zoom) && Array.isArray(v.bounds);
  const isStyle = (s) => s && s.version === 8 && s.sources && s.sources.protomaps && typeof s.sources.protomaps.url === 'string' && Array.isArray(s.layers);
  const isOverlays = (o) => o && isView(o.view) && o.municipalities && o.contours && Array.isArray(o.context);
  const isLngLat = (p) => p && Number.isFinite(p.lng) && Number.isFinite(p.lat);

  const POSTER_WIDTH = { landscape: 600, portrait: 400 }; // CSS px the posters were rendered at (build.mjs POSTERS)
  const HIT_PAD = 4;
  let instances = 0;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  class LabLandMap extends HTMLElement {
    connectedCallback() {
      // a move (disconnect then connect in the same task, as the theme editor does on reorder)
      // keeps the live map, its listeners and its observers: teardown was deferred and skipped
      if (this.abort && !this.abort.signal.aborted) return;
      this.abort = new AbortController();
      this.warned = false;
      this.uid ??= ++instances;
      this.map = null;
      this.state = 'idle'; // idle → loading → ready | failed
      this.readMarks();
      // a lot found on the page (assets/lab-lot.js) names its field: selected at once on the marks,
      // framed on the map once it is up
      this.selection = document.documentElement.dataset.lotField
        ? { name: document.documentElement.dataset.lotField, code: document.documentElement.dataset.lotCode || '', animate: false }
        : null;
      document.addEventListener('lab:lot-field', (event) => this.select(event.detail || {}), { signal: this.abort.signal });
      if (this.selection) this.markCurrent(this.selection.name);
      if ('ResizeObserver' in window) {
        // fires once after the first layout (before paint), then on every size change
        this.resizer = new ResizeObserver(() => this.declutter());
        this.resizer.observe(this);
      } else {
        this.declutter();
      }
      document.fonts?.ready.then(() => { if (!this.abort.signal.aborted) this.declutter(); });
      // the map block's interaction: poster = the image and its marks are the map, never loaded;
      // tap = always wait for the button; auto = wait below 1024 (see the header), mount on approach above
      const interaction = this.dataset.interaction || 'auto';
      if (interaction === 'poster') return;
      const loader = this.querySelector('[data-map-load]');
      if (loader && (interaction === 'tap' || window.matchMedia('(width < 1024px)').matches)) {
        loader.hidden = false;
        loader.addEventListener('click', () => {
          loader.hidden = true;
          this.mount();
        }, { once: true, signal: this.abort.signal });
        return;
      }
      if (!('IntersectionObserver' in window)) {
        this.mount();
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.mount();
      }, { rootMargin: '300px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      // deferred so a move is not a teardown: build() has already dropped the poster and handed
      // the server marks to the map, so a torn-down map could not be rebuilt on reconnect
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.abort.abort();
        this.observer?.disconnect();
        this.resizer?.disconnect();
        this.map?.remove();
        this.map = null;
        this.querySelector('[data-map-canvas]')?.remove();
      });
    }

    warn(message, error) {
      if (this.warned) return;
      this.warned = true;
      console.warn(`lab-land-map: ${message}`, error ?? '');
    }

    fail(error) {
      if (this.abort.signal.aborted) return;
      this.state = 'failed';
      this.dataset.state = 'failed';
      this.map?.remove();
      this.map = null;
      this.querySelector('[data-map-canvas]')?.remove();
      this.warn('the map could not load; the poster stays.', error);
    }

    // the server-rendered marks (snippets/lab-land-map-marks.liquid)
    // (names the map block switched off are not drawn, so they are neither measured nor handed over)
    readMarks() {
      const box = this.querySelector('.lmap__marks');
      const figure = this.closest('.lmap');
      const off = (name) => figure?.classList.contains(`lmap--no-${name}`);
      this.marks = box && box.querySelector('.lmap__mill') ? {
        box,
        context: off('context') ? [] : [...box.querySelectorAll('.lmap__ctx')],
        towns: off('towns') ? [] : [...box.querySelectorAll('.lmap__town')],
        mill: box.querySelector('.lmap__mill'),
        fields: [...box.querySelectorAll('.lmap__field')],
      } : null;
    }

    readData() {
      const island = this.querySelector('script[data-land-map]');
      const data = JSON.parse(island ? island.textContent : 'null');
      if (!data || !isLngLat(data.mill) || !Array.isArray(data.towns) || !Array.isArray(data.fields)) throw new Error('bad data island');
      return { ...data, fields: data.fields.filter((f) => isLngLat(f) && Array.isArray(f.lots) && f.lots.length), strings: data.strings || {} };
    }

    async mount() {
      if (this.state !== 'idle') return;
      this.state = 'loading';
      this.dataset.state = 'loading';
      const { signal } = this.abort;
      try {
        const data = this.readData();
        const [maplibregl, style, overlays, arches] = await Promise.all([
          loadLibs(this),
          fetchJson(this.dataset.style, signal),
          fetchJson(this.dataset.overlays, signal),
          fetchText(this.dataset.arches, signal),
        ]);
        if (signal.aborted) return;
        if (!isStyle(style)) throw new Error('bad style');
        if (!isOverlays(overlays)) throw new Error('bad overlays');
        const tiles = new URL(this.dataset.tiles, `${this.dataset.tilesOrigin || location.origin}/`).href;
        style.sources.protomaps.url = style.sources.protomaps.url.replace('__PMTILES__', tiles);
        await this.build(maplibregl, style, overlays, arches, data);
      } catch (error) {
        if (error?.name === 'AbortError' || signal.aborted) return;
        this.fail(error);
      }
    }

    async build(maplibregl, style, overlays, arches, data) {
      const { signal } = this.abort;
      const { view } = overlays;
      const S = data.strings;
      const css = getComputedStyle(this);
      const C = {
        ink: toRgb(css.getPropertyValue('--ink'), '#1d1d1b'),
        sage: toRgb(css.getPropertyValue('--sage'), '#d0e2a6'),
        sageDeep: toRgb(css.getPropertyValue('--sage-deep'), '#bcd89c'),
      };

      const canvas = el('div', 'lmap__canvas');
      canvas.dataset.mapCanvas = '';
      this.prepend(canvas);

      // the poster's camera, scaled to the stage (assets/lab-section-map.css --pw)
      const portrait = this.clientHeight > this.clientWidth;
      const posterWidth = portrait ? POSTER_WIDTH.portrait : POSTER_WIDTH.landscape;
      const zoom = Math.min(view.maxZoom, Math.max(view.minZoom, view.zoom + Math.log2((this.clientWidth || posterWidth) / posterWidth)));

      const map = new maplibregl.Map({
        container: canvas,
        style,
        center: view.center,
        zoom,
        minZoom: view.minZoom,
        maxZoom: view.maxZoom,
        maxBounds: view.bounds,
        cooperativeGestures: true,
        attributionControl: false,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        fadeDuration: 0,
        locale: {
          'Map.Title': S.label || 'Map',
          'NavigationControl.ZoomIn': S.zoom_in || 'Zoom in',
          'NavigationControl.ZoomOut': S.zoom_out || 'Zoom out',
          'Popup.Close': S.close || 'Close',
          'CooperativeGesturesHandler.WindowsHelpText': S.gesture_ctrl || 'Use Ctrl + scroll to zoom the map',
          'CooperativeGesturesHandler.MacHelpText': S.gesture_mac || 'Use ⌘ + scroll to zoom the map',
          'CooperativeGesturesHandler.MobileHelpText': S.gesture_touch || 'Use two fingers to move the map',
        },
      });
      this.map = map;
      map.touchZoomRotate.disableRotation();
      map.keyboard.disableRotation();
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');
      map.addControl(new maplibregl.ScaleControl({ maxWidth: 90, unit: 'metric' }), 'bottom-left');
      // no AttributionControl: the stage's own © OpenStreetMap link becomes a control at the swap

      // Any error before the first idle fails the mount (the poster stays); after it, one warning.
      let early = null;
      let reject = null;
      map.on('error', (event) => {
        const error = event?.error || new Error('map error');
        if (this.state === 'ready') {
          this.warn('a map resource failed to load.', error);
          return;
        }
        early ??= error;
        reject?.(early);
      });
      const until = (type) => new Promise((resolve, rejectWith) => {
        if (early) { rejectWith(early); return; }
        reject = rejectWith;
        map.once(type, () => { reject = null; resolve(); });
      });

      await until('load');
      if (signal.aborted) return;

      // the label's arches in sage-deep (180 × 90 source at pixelRatio 2 → a 90 × 45 CSS px tile)
      const image = new Image();
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(arches.replaceAll('#000', C.sageDeep))}`;
      await image.decode();
      if (signal.aborted) return;
      map.addImage('arches', image, { pixelRatio: 2 });

      const roads = map.getStyle().layers.find((layer) => /^roads/.test(layer.id))?.id;
      // the map block's switches (the data island's `layers`; absent means on)
      const layers = { municipalities: true, lines: true, ...data.layers };
      map.addSource('municipalities', { type: 'geojson', data: overlays.municipalities });
      if (layers.municipalities) {
        map.addLayer({ id: 'municipalities-ground', type: 'fill', source: 'municipalities', paint: { 'fill-color': C.sage, 'fill-opacity': 0.75 } }, roads);
        map.addLayer({ id: 'municipalities-pattern', type: 'fill', source: 'municipalities', paint: { 'fill-pattern': 'arches', 'fill-opacity': 0.9 } }, roads);
      }
      map.addSource('contours', { type: 'geojson', data: overlays.contours });
      map.addLayer({
        id: 'contours', type: 'line', source: 'contours',
        paint: { 'line-color': C.ink, 'line-opacity': ['case', ['get', 'index'], 0.3, 0.12], 'line-width': ['case', ['get', 'index'], 0.8, 0.5] },
      }, roads);
      if (layers.municipalities) {
        map.addLayer({ id: 'municipalities-line', type: 'line', source: 'municipalities', paint: { 'line-color': C.ink, 'line-width': 1.2, 'line-dasharray': [1, 2.5] } });
      }

      const mill = [data.mill.lng, data.mill.lat];
      if (data.fields.length && layers.lines) {
        map.addSource('routes', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: data.fields.map((f) => ({ type: 'Feature', properties: { name: f.name.trim().toLowerCase() }, geometry: { type: 'LineString', coordinates: [[f.lng, f.lat], mill] } })) },
        });
        map.addLayer({ id: 'routes', type: 'line', source: 'routes', layout: { 'line-cap': 'round' }, paint: { 'line-color': C.ink, 'line-width': 2, 'line-dasharray': [0.1, 2.5] } });
      }

      await until('idle');
      if (signal.aborted) return;
      if (document.fonts) await document.fonts.ready;
      if (signal.aborted) return;
      if (early) throw early;
      if (!this.marks) throw new Error('no marks in the stage');

      // the swap, in one task: the marks move onto the map at the same pixels, the poster goes
      this.handOver(maplibregl, map, data);
      this.querySelector('[data-poster]')?.remove();
      this.declutter();
      map.on('moveend', () => this.declutter());
      this.state = 'ready';
      this.dataset.state = 'ready';
      this.home = { center: map.getCenter(), zoom: map.getZoom() };
      this.maplibregl = maplibregl;
      if (this.selection) this.frame({ ...this.selection, animate: false });
    }

    // The lot the reader looked up (assets/lab-lot.js): { name, code, animate }, or { name: null }
    // for a miss. The marks change at once, poster or map; the camera follows once the map is up.
    select(detail) {
      const name = typeof detail.name === 'string' && detail.name.trim() ? detail.name : null;
      this.selection = name ? { name, code: detail.code || '', animate: Boolean(detail.animate) } : null;
      this.markCurrent(name);
      if (this.state === 'ready' && this.map) this.frame(this.selection);
    }

    markCurrent(name) {
      if (!this.marks) return;
      const wanted = name ? name.trim().toLowerCase() : null;
      for (const node of this.marks.fields) {
        const label = (node.querySelector('.lmap__field-name')?.textContent || '').trim().toLowerCase();
        const current = Boolean(wanted) && label === wanted;
        node.classList.toggle('lmap__field--current', current);
        if (node.tagName === 'BUTTON') {
          if (current) node.setAttribute('aria-current', 'true');
          else node.removeAttribute('aria-current');
        }
      }
    }

    // A field and the mill in one frame, its connecting line drawn full and the others faded; with no
    // selection, the default view and every line alike. A field with no position is not on the map:
    // the view is left as it is.
    frame(selection) {
      const map = this.map;
      if (!map) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const wanted = selection ? selection.name.trim().toLowerCase() : null;
      const field = wanted ? (this.fieldData || []).find((f) => f.name.trim().toLowerCase() === wanted) : null;
      if (map.getLayer('routes')) {
        const on = field ? ['==', ['get', 'name'], wanted] : true;
        map.setPaintProperty('routes', 'line-opacity', field ? ['case', on, 1, 0.25] : 1);
        map.setPaintProperty('routes', 'line-width', field ? ['case', on, 2.5, 1.5] : 2);
      }
      this.closeCard(false);
      if (!field) {
        if (!selection && this.home && this.framed) map.jumpTo(this.home);
        this.framed = false;
        return;
      }
      const mill = this.millLngLat;
      const bounds = new this.maplibregl.LngLatBounds([field.lng, field.lat], [field.lng, field.lat]).extend(mill);
      const pad = Math.round(Math.min(this.clientWidth, this.clientHeight) * 0.18);
      const move = { padding: { top: pad, bottom: pad, left: pad, right: pad }, maxZoom: 13 };
      if (selection.animate && !reduced) map.fitBounds(bounds, { ...move, duration: 700 });
      else map.fitBounds(bounds, { ...move, animate: false });
      this.framed = true;
    }

    // The server-rendered marks become MapLibre markers: the same nodes, with the anchors their CSS
    // transforms mirror (assets/lab-section-map.css .lmap__pin), positioned to the subpixel as the CSS
    // did. The fields' decorative copies are replaced by real buttons in the same place.
    handOver(maplibregl, map, data) {
      const { signal } = this.abort;
      const S = data.strings;
      this.strings = S;
      const add = (node, lngLat, options, decorative = true) => {
        node.classList.remove('lmap__pin');
        const marker = new maplibregl.Marker({ element: node, subpixelPositioning: true, ...options }).setLngLat(lngLat).addTo(map);
        // Marker.addTo labels every element "Map marker"; decorative names are in the info band
        node.removeAttribute('aria-label');
        if (decorative) node.setAttribute('aria-hidden', 'true');
        return marker;
      };
      const at = (node) => [Number(node.dataset.lng), Number(node.dataset.lat)];

      const { context, towns, mill, fields, box } = this.marks;
      for (const node of context) add(node, at(node), { anchor: 'center' });
      for (const node of towns) add(node, at(node), { anchor: 'center' });
      add(mill, [data.mill.lng, data.mill.lat], { anchor: 'bottom', offset: [0, 22] });
      for (const node of fields) node.remove();

      const attrib = this.querySelector('[data-attrib]');
      if (attrib) map.addControl({ onAdd: () => attrib, onRemove: () => attrib.remove() }, 'bottom-right');

      this.popup = null;
      this.fieldData = data.fields;
      this.millLngLat = [data.mill.lng, data.mill.lat];
      this.marks.fields = data.fields.map((field, index) => {
        const button = el('button', 'lmap__field');
        button.type = 'button';
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-expanded', 'false');
        const tag = el('span', 'lmap__field-tag');
        // the space keeps the accessible name "CAMPO • FIELD Fontanacce" (the grid ignores it visually)
        tag.append(el('span', 'lmap__field-pair', S.pair_field || 'CAMPO • FIELD'), ' ', el('span', 'lmap__field-name', field.name));
        button.append(el('i', 'lmap__field-sq'), tag);
        add(button, [field.lng, field.lat], { anchor: 'left', offset: [-7, 0] }, false);
        button.addEventListener('click', (event) => {
          event.stopPropagation(); // the map's own click would close the card it is opening
          this.toggleCard(maplibregl, map, field, button, `lmap-card-${this.uid}-${index}`);
        }, { signal });
        return button;
      });
      box.remove();
      if (this.selection) this.markCurrent(this.selection.name);

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.popup) return;
        event.stopPropagation();
        this.closeCard(true);
      }, { signal });
    }

    card(field, id, S) {
      const card = el('div', 'lmap-card');
      card.id = id;
      card.setAttribute('role', 'dialog');
      card.setAttribute('aria-label', field.name);
      const head = el('div', 'lmap-card__head');
      head.append(el('span', 'lmap-card__pair', S.pair_field || 'CAMPO • FIELD'), ' ', el('span', 'lmap-card__name', field.name));
      card.append(head);
      const list = el('ul', 'lmap-card__lots');
      for (const lot of field.lots) {
        if (typeof lot?.code !== 'string' || typeof lot?.url !== 'string') continue;
        const row = el('li', 'lmap-card__row');
        const link = el('a', 'lmap-card__code mono', lot.code);
        link.href = lot.url;
        if (this.selection && this.selection.code && lot.code === this.selection.code) link.setAttribute('aria-current', 'true');
        link.append(el('span', 'arrow', '→'));
        link.lastChild.setAttribute('aria-hidden', 'true');
        row.append(el('span', 'lmap-card__pair', S.lot || 'LOTTO • LOT'), ' ', link);
        list.append(row);
      }
      card.append(list);
      if (S.delivered) card.append(el('p', 'lmap-card__foot', S.delivered));
      return card;
    }

    toggleCard(maplibregl, map, field, button, id, focus = true) {
      const same = this.popup && this.popupButton === button;
      this.closeCard(false);
      if (same) return;
      // MapLibre picks an anchor per side but cannot keep a 280 px card inside a 340 px map when the
      // field sits near an edge: the anchor is locked above or below the field (wherever there is
      // more room) and the card is shifted sideways, once, to stay inside the frame. No camera move.
      const GAP = 18;
      const point = map.project([field.lng, field.lat]);
      const anchor = point.y > map.getContainer().clientHeight / 2 ? 'bottom' : 'top';
      const dy = anchor === 'bottom' ? -GAP : GAP;
      const popup = new maplibregl.Popup({ anchor, offset: [0, dy], closeButton: true, closeOnClick: true, focusAfterOpen: focus, maxWidth: 'min(300px, calc(100cqi - 24px))', className: 'lmap-popup' }) // 100cqi: the .lmap figure's width
        .setLngLat([field.lng, field.lat])
        .setDOMContent(this.card(field, id, this.strings));
      popup.on('close', () => {
        button.setAttribute('aria-expanded', 'false');
        button.removeAttribute('aria-controls');
        if (this.popup === popup) { this.popup = null; this.popupButton = null; }
      });
      this.popup = popup;
      this.popupButton = button;
      popup.addTo(map);
      // the card's own × removes the popup (and its focus with it): focus goes back to the field, as on
      // Escape. A click on the map (closeOnClick) leaves focus where the click put it.
      popup.getElement().querySelector('.maplibregl-popup-close-button')
        ?.addEventListener('click', () => button.focus(), { once: true, signal: this.abort.signal });
      const frame = map.getContainer().getBoundingClientRect();
      const box = popup.getElement().getBoundingClientRect();
      const pad = 8;
      let dx = 0;
      if (box.right > frame.right - pad) dx = frame.right - pad - box.right;
      if (box.left + dx < frame.left + pad) dx = frame.left + pad - box.left;
      if (dx) popup.setOffset([Math.round(dx), dy]);
      button.setAttribute('aria-expanded', 'true');
      button.setAttribute('aria-controls', id);
    }

    closeCard(returnFocus) {
      if (!this.popup) return;
      const button = this.popupButton;
      this.popup.remove();
      if (returnFocus) button?.focus();
    }

    // Towns, the mill and fields always show. A town whose square falls under the mill's disc
    // (Villa San Giovanni, ~300 m from the mill) starts its name past the disc; a town name that would
    // run under the disc or the mill's tag (Blera, 1.7 km west-south-west, on a narrow stage) goes to
    // the other side of its square; a name that would run out of the frame wraps, hanging down from
    // the square's line. Context names:
    // villages before hamlets, hidden when they would touch a shown name or leave the frame.
    // The same nodes are measured before and after the swap, so the poster and the map agree.
    declutter() {
      if (!this.marks) return;
      const frame = this.getBoundingClientRect();
      if (!frame.width) return;
      const { towns, mill, fields, context } = this.marks;
      const hit = (a, b, pad = HIT_PAD) => a.left - pad < b.right && b.left - pad < a.right && a.top - pad < b.bottom && b.top - pad < a.bottom;
      const disc = mill.lastElementChild.getBoundingClientRect();
      // The mill's tag sits centred over the disc; a field close to the mill (Piangajardo is ~25 px away at the
      // wide default zoom) would sit under it, so the tag slides to the side away from that field. The disc stays.
      mill.classList.remove('lmap__mill--east', 'lmap__mill--west');
      const centred = mill.firstElementChild.getBoundingClientRect();
      const under = fields.map((node) => node.firstElementChild.getBoundingClientRect()).filter((sq) => hit(sq, centred));
      if (under.length) {
        const middle = disc.left + disc.width / 2;
        mill.classList.add(under.some((sq) => sq.left + sq.width / 2 < middle) ? 'lmap__mill--east' : 'lmap__mill--west');
      }
      const tag = mill.firstElementChild.getBoundingClientRect();
      // Fields first — they are what the map is for: a field's tag reads east of its square unless it would
      // cover the mill, an earlier field or the frame's edge; then it reads west (--flip). The square never moves.
      const fieldBoxes = [];
      for (const field of fields) {
        field.classList.remove('lmap__field--flip');
        const box = field.querySelector('.lmap__field-tag')?.getBoundingClientRect();
        if (box && (hit(box, disc) || hit(box, tag) || fieldBoxes.some((f) => hit(box, f)) || box.right > frame.right - 4)) {
          field.classList.add('lmap__field--flip');
        }
        for (const part of field.children) fieldBoxes.push(part.getBoundingClientRect());
      }
      const shown = [mill.getBoundingClientRect(), ...fieldBoxes];
      // nearest the mill first: at narrow stages two towns can sit under the disc (Villa San Giovanni, the mill's
      // own village, and Blera); the nearer one keeps the slot east of the disc, the next one reads west
      const middleX = disc.left + disc.width / 2, middleY = disc.top + disc.height / 2;
      const distance = (node) => { const r = node.getBoundingClientRect(); return Math.hypot(r.left + r.width / 2 - middleX, r.top + r.height / 2 - middleY); };
      for (const town of [...towns].sort((a, b) => distance(a) - distance(b))) {
        const name = town.lastElementChild;
        name.style.whiteSpace = '';
        name.style.maxInlineSize = '';
        name.style.marginInlineStart = '';
        town.classList.remove('lmap__town--under', 'lmap__town--flip', 'lmap__town--wrap');
        const square = town.getBoundingClientRect();
        if (hit(square, disc, 0)) {
          town.classList.add('lmap__town--under');
          name.style.marginInlineStart = `${Math.ceil(disc.right + 4 - name.getBoundingClientRect().left)}px`;
          if (shown.slice(1).some((s) => hit(name.getBoundingClientRect(), s))) {
            // east of the disc is taken: the name reads west of its own square instead
            town.classList.remove('lmap__town--under');
            name.style.marginInlineStart = '';
            town.classList.add('lmap__town--flip');
          }
        } else {
          const r = name.getBoundingClientRect();
          if (hit(r, disc) || hit(r, tag) || fieldBoxes.some((f) => hit(r, f))) town.classList.add('lmap__town--flip');
        }
        const flipped = town.classList.contains('lmap__town--flip');
        const r = name.getBoundingClientRect();
        const room = !flipped && r.left < frame.right && r.right > frame.right - 6 ? frame.right - 6 - r.left
          : flipped && r.right > frame.left && r.left < frame.left + 6 ? r.right - frame.left - 6 : 0;
        if (room) {
          // a wrapped name hangs from its first line, level with the square: it grows away from the mill's tag
          town.classList.add('lmap__town--wrap');
          name.style.whiteSpace = 'normal';
          name.style.maxInlineSize = `${Math.max(40, Math.floor(room))}px`;
        }
        shown.push(square, name.getBoundingClientRect());
      }
      const ordered = [...context].sort((a, b) => (a.dataset.kind === 'village' ? 0 : 1) - (b.dataset.kind === 'village' ? 0 : 1));
      for (const node of ordered) node.style.visibility = '';
      for (const node of ordered) {
        const r = node.getBoundingClientRect();
        const inside = r.left >= frame.left && r.right <= frame.right && r.top >= frame.top && r.bottom <= frame.bottom;
        if (!inside || shown.some((s) => hit(r, s))) node.style.visibility = 'hidden';
        else shown.push(r);
      }
    }
  }

  customElements.define('lab-land-map', LabLandMap);
})();
